import { CSSProperties, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import millify from "millify";

// Components
import UpgradeToProSucceededModal from "components/shareable/UpgradeToProSucceededModal/UpgradeToProSucceededModal";
import ButtonSymphony from "elements/ButtonSymphony";
import Recommendations from "components/shareable/Recommendations";
import CampaignsSidebar, { adaptCampaignsForSidebar } from "components/shareable/CampaignsSidebar/CampaignsSidebar";
import WelcomeToSymphony from "components/WelcomeToSymphony";
import ModalSymphony from "elements/ModalSymphony";
import PageSymphony from "elements/PageSymphony";
import ContentSymphony from "components/shareable/ContentSymphony";
import DefaultLearningIndicator, { LearningIndicatorType } from "components/shareable/LearningIndicator/DefaultLearningIndicator";
import CardSymphony from "elements/CardSymphony";
import SmallGraph from "components/dashboard/SmallGraph";

// Hooks
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useUser, { UserExistsState } from 'Hooks/useUser';
import useScreen from "Hooks/useScreen";
import { GraphPresentation, useDashboard } from "Hooks/useDashboard";

// Utils & Constants
import { track } from "analytics";
import { generateShortUniqueId, getStatusToShowPaymentFailedBanner, makeKey, styx } from "modules/Utils";
import Colors from "modules/Colors";
import { AdConnectionType, AdPlatform, PlaybookType, SocialConnectType, WebsiteTemplateCategory } from "modules/Const";
import { setCookie } from "modules/Utils";
import { CurrentBrand, SpotifyArtistInBrandConnections } from "modules/Types";

// Using shared adapter from CampaignsSidebar

// Styles
import "styles/react-datepicker.css";
import { Fade, SvgIcon } from "@material-ui/core";
import OverallDashboard from "./OverallDashboard";
import useOnboardingV3 from "Hooks/useOnboardingV3"; // For checking onboarding status
import PlatformsConnector from "./PlatformsConnector";
import SocialConnectModal from "components/shareable/SocialConnectModal";
import LargeGraphPopup from "./components/LargeGraphPopup";
import { connectToBrand_api } from 'api/brandApi';
import Loader from 'elements/Loader';
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import Sty from "modules/Sty";
import useWebsiteBuilder from "Hooks/useWebsiteBuilder";
import useGlobalModals from "Hooks/useGlobalModals";
import { useCampaignModal } from "Hooks/useGlobalModals";
import Icon from "elements/Icon";
import AdConnectionModal from "components/shareable/AdConnectionModal";

const SHOW_ACTIVE_CAMPAIGNS = true;

enum DashboardSideContent {
    INSTAGRAM = 'instagram',
    YOUTUBE = 'youtube',
    FACEBOOK = 'facebook',
    TIKTOK = 'tiktok',
    SOUNDCLOUD = 'soundcloud',
    DEEZER = 'deezer',
}

export interface FanbaseMetricsType {
    totalFanbase: number;
    foreverPresaveFans: number;
    fanbaseGrowth: string;
    foreverPresaveFansGrowth: string;
    dailyMetrics: {
        [key: string]: {
            totalFans: number;
            foreverSaves: number;
        }
    };
}

// TODO: ask megh for icons - music-player by default
export const siteIconsData: { [key: string]: any; } = {
    deezer: require("assets/images/platforms/deezer.svg").default,
    spotify: require("assets/images/platforms/spotify.svg").default,
    tidal: require("assets/images/platforms/tidal.svg").default,
    youtube: require("assets/images/platforms/youtube.svg").default,
    apple_music: require("assets/images/music-player.svg").default,
    amazonStore: require("assets/images/music-player.svg").default,
    amazon_music: require("assets/images/music-player.svg").default,
    instagram: require("assets/images/platforms/instagram.svg").default,
    tiktok: require("assets/images/platforms/tiktok.svg").default,
    facebook: require("assets/images/platforms/facebook.svg").default,
    soundcloud: require("assets/images/platforms/soundcloud.svg").default,
    twitter: require("assets/images/twitter.svg").default,
    audiomack: require("assets/images/platforms/audiomack.svg").default,
    fans: require("assets/images/platforms/fans.svg").default,
    'forever-saves': require("assets/images/platforms/forever-saves.svg").default,
}

const Dashboard = () => {
    const history = useHistory();
    const {
        smallView,
        mediumView,
        mStyle,
        cssCalc,
        mobileView
    } = useScreen();
    const { currentBrand, isBrandAdmin, isGrandfathered } = useBrand() as BrandExistsState
    const { isSymphonyBetaTestUser } = useUser() as UserExistsState;
    const {
        getDashboardData,
        isLoadingInitialData,
        timeRange,
        selectedDate,
        platformGraphsData,
        selectedPlatformForConnect,
        setSelectedPlatformForConnect,
        isLoadingCampaignsRecent,
        campaignsRecent,
        getCampaignsRecent
    } = useDashboard();


    const {
        setNewCampaignAdPlatformFilter
    } = useCampaignBuilder();

    const { openCreateWebsite } = useGlobalModals();
    const { openCampaignModal } = useCampaignModal();

    const websiteSlug = generateShortUniqueId()

    const commonTrackBrandData = {
        brand_id: currentBrand.id,
        brand_name: currentBrand.name,
        brand_slug: currentBrand.slug,
    }

    const handleNewWebsite = () => {
        openCreateWebsite({
            templateCategory: WebsiteTemplateCategory.POPULAR,
            source: 'dashboard_page_new_website'
        })
    }

    const PLAYBOOKS_FOR_DASHBOARD: {
        name: string;
        icon: string;
        title: string;
        background: string;
        onClick: () => void;
        hidden?: boolean;
        onlyForSymphonyBetaTestUsers?: boolean;
    }[] = [
            {
                name: 'boostInstagramEngagement',
                icon: 'instagram',
                title: 'Run IG Ads',
                background: Colors.gradientInstagram,
                onClick: () => {
                    openCampaignModal({
                        source: 'TikTok Playbook Card - Dashboard',
                        adPlatform: AdPlatform.META
                    })
                    setNewCampaignAdPlatformFilter(AdPlatform.META)
                    const commonTrackData = {
                        adPlatform: AdPlatform.META,
                        playbookType: PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                }
            },
            {
                name: 'runTikTokAds',
                icon: 'tiktok',
                title: 'Run TikTok Ads',
                background: Colors.gradientTikTok,
                onClick: () => {
                    openCampaignModal({
                        source: 'TikTok Playbook Card - Dashboard',
                        adPlatform: AdPlatform.TIKTOK
                    })
                    const commonTrackData = {
                        adPlatform: AdPlatform.TIKTOK,
                        playbookType: PlaybookType.TIKTOK_BOOST_ENGAGEMENT,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                }
            },
            {
                name: 'createPreSave',
                icon: 'preSave',
                title: 'Create a Pre-save',
                background: Colors.gradientPresave,
                onClick: () => {
                    const commonTrackData = {
                        playbookType: PlaybookType.META_PRESAVES,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)

                    history.push('/marketing/new/presave')
                }
            },
            {
                name: 'youtubeAds',
                icon: 'youtubeFlat',
                title: 'Run YouTube Ads',
                hidden: false,
                background: Colors.gradientYouTube,
                onClick: () => {
                    const commonTrackData = {
                        playbookType: PlaybookType.INCREASE_VIDEO_VIEWS,
                        ...commonTrackBrandData,
                    }
                    track("Campaign Started", commonTrackData)
                    track("Playbook Card Selection", commonTrackData)
                    history.push('/marketing/new/video-views')
                }
            },
            {
                name: 'buildWebsite',
                icon: 'musicSite',
                title: 'Build a Website',
                background: Colors.gradientWebsite,
                onClick: handleNewWebsite
            },
        ]

    /** Controls visibility of the welcome modal */
    const [isFirstDashboardDataLoad, setIsFirstDashboardDataLoad] = useState<boolean>(true)
    const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(false);

    const [sessionId, setSessionId] = useState(String())
    const [showUpgradeToProSucceededModal, setShowUpgradeToProSucceededModal] = useState<boolean>(false)

    // TODO: Set this to true when we have recommendations sidebar ready to go
    const [showRecommendationsSidebar, setShowRecommendationsSidebar] = useState<boolean>(false)

    // edit platforms modal
    const [showLargeGraph, setShowLargeGraph] = useState<GraphPresentation | null>(null)
    const [showSocialConnectModal, setShowSocialConnectModal] = useState<boolean>(false)

    /** Controls visibility of the TikTok auth modal */
    const [showTikTokAuthModal, setShowTikTokAuthModal] = useState<boolean>(false);
    const [isTikTokConnecting, setIsTikTokConnecting] = useState<boolean>(false);
    const [tikTokError, setTikTokError] = useState<string | null>(null);

    useEffect(() => {
        if (currentBrand?.dashboard_initialized && selectedDate) {
            track('Updated Time Range', {
                name: 'Dashboard',
                to: timeRange,
                startDate: selectedDate.startDate,
                endDate: selectedDate.endDate,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name
            });
        }
    }, [selectedDate, timeRange]);

    const getInitialDashboardAndCampaignData = async () => {
        setIsFirstDashboardDataLoad(true)
        try {
            await getDashboardData({
                startDate: selectedDate.startDate,
                endDate: selectedDate.endDate
            })

            if (!isLoadingCampaignsRecent) {
                await getCampaignsRecent({
                    brandId: currentBrand.id,
                    // isAnalytics: true
                })
            }
        } catch (error) {
            console.error('Error getting initial dashboard and campaign data', error)
        } finally {
            setIsFirstDashboardDataLoad(false)
        }
    }

    useEffect(() => {
        if (currentBrand) {
            getInitialDashboardAndCampaignData()
        }

        const authCode = localStorage.getItem('tt_auth_code');
        if (authCode && currentBrand?.id) {
            setShowTikTokAuthModal(true);
            handleTikTokAuth(authCode);
        }
    }, [currentBrand]);

    const checkSubscriptionStatus = async () => {
        const urlParams = new URLSearchParams(window.location.search)
        const subscription = urlParams.get("subscription")
        const brand = urlParams.get("brand")
        const session_id = urlParams.get("session_id")

        if (
            session_id &&
            subscription === "succeeded" &&
            brand === currentBrand?.slug
        ) {
            setSessionId(session_id)
            setShowUpgradeToProSucceededModal(true)
        } else {
            setSessionId(String())
            setShowUpgradeToProSucceededModal(false)
        }
    }

    const closeUpgradeToProSucceededModal = () => {
        setSessionId(String())
        setShowUpgradeToProSucceededModal(false)
    }

    const handleItemClick = (itemType: DashboardSideContent) => {
        switch (itemType) {
            case DashboardSideContent.INSTAGRAM:
                // TODO: Handle Instagram click
                break;
            case DashboardSideContent.YOUTUBE:
                // TODO: Handle YouTube click
                break;
            case DashboardSideContent.FACEBOOK:
                // TODO: Handle Facebook click
                break;
            case DashboardSideContent.TIKTOK:
                // TODO: Handle TikTok click
                break;
            case DashboardSideContent.SOUNDCLOUD:
                // TODO: Handle SoundCloud click
                break;
            case DashboardSideContent.DEEZER:
                // TODO: Handle Deezer click
                break;
            default:
                break;
        }
    };



    const renderPageHeader = () => {
        return (
            <CardSymphony
                className="overallDashboardComponentCardTitle"
                title="Fanbase Growth"
                subtitle={
                    <div
                        className="overallDashboardComponentCardTitleSubtitle"
                        style={{
                            display: 'flex',
                            flexDirection: 'row'
                        }}
                    >
                        <div>
                            Track how your fanbase is growing across the board.
                        </div>
                    </div>
                }
                children={
                    <DefaultLearningIndicator
                        text="Learn how to use your Dashboard"
                        type={LearningIndicatorType.DASHBOARD}
                        style={{ marginBottom: 8 }}
                    />
                }
                isNoPadding
            />
        )
    }

    const renderLargeGraphPopup = () => {
        return (
            <ModalSymphony
                className="overallDashboardLargeGraphPopup"
                isOpen={showLargeGraph && showLargeGraph.shown ? true : false}
                onClose={() => setShowLargeGraph({
                    ...showLargeGraph!,
                    shown: false
                })}
                style={{ zIndex: 998 }}
                maxWidth={900}
            >
                {showLargeGraph &&
                    <Fade in={showLargeGraph.shown ? true : false}>
                        <LargeGraphPopup
                            platformId={showLargeGraph.platformId}
                            platformName={showLargeGraph.platformName}
                            statName={showLargeGraph.statName}
                            statIdentifier={showLargeGraph.statIdentifier}
                            key={showLargeGraph.key}
                            imageAsset={showLargeGraph.imageAsset}
                            primaryValue={showLargeGraph.currentValue}
                            chartData={showLargeGraph.chartData}
                            changeValue={showLargeGraph.changeValue}
                            onClose={() => setShowLargeGraph({
                                ...showLargeGraph!,
                                shown: false
                            })}
                        />
                    </Fade>
                }
            </ModalSymphony>
        )
    }

    const renderOverallDashboard = () => {
        return (
            <>

                {!mediumView &&
                    renderPageHeader()
                }
                <div className="mx-auto relative overallDashboardComponent">
                    <OverallDashboard />
                </div>
                {mediumView &&
                    <div className="mt-4">
                        <DefaultLearningIndicator
                            text="Learn how to use your Dashboard"
                            type={LearningIndicatorType.DASHBOARD}
                        />
                    </div>
                }
            </>
        )
    }

    const renderSideContentInner = () => {
        return (
            <div className="dashboardSideItemsContainer" style={mStyle(sty, 'dashboardSideItemsContainer')}>
                <CampaignsSidebar
                    className="dashboardCampaignsSidebar"
                    campaigns={adaptCampaignsForSidebar(campaignsRecent)}
                    isLoading={isLoadingCampaignsRecent}
                    showBrandInfo={false}
                    showRecommendations={true}
                />
            </div>
        )
    }

    const renderUpdateNotifications = () => {
        return (
            <div className="updateNotificationsContainer" style={mStyle(sty, 'updateNotificationsContainer')}>
                <div style={{ marginRight: 'auto', marginBottom: 8 }}>
                    Recommendations:
                </div>
                <Recommendations
                    isRecommdationVertical
                    isBanner={mediumView}
                />
                {/* <div className="updateNotificationsCount" style={sty.updateNotificationsCount}>
                    {dashboardSideContentArray.length}
                </div> */}
            </div>
        )
    }

    const renderDashboardSideContent = () => {
        if (!isBrandAdmin) {
            return null;
        }

        return (
            <div
                className="dashboardSideContentContainer"
                style={styx(
                    mStyle(sty, 'dashboardSideContentContainer'),
                    { maxHeight: cssCalc(48) }
                )}
            >
                <div className="sideDashboardButtonsContainer" style={mStyle(sty, 'sideDashboardButtonsContainer')}>
                    <ButtonSymphony
                        className="newWebsiteSideDashboardButton"
                        onClick={handleNewWebsite}
                        variant="outlined"
                        width="100%"
                        size="small"
                        iconLeft="plus"
                        iconLeftSize={18}
                        iconLeftOffset={-6}
                    >
                        New Website
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="newCampaignSideDashboardButton"
                        onClick={() => {
                            openCampaignModal({
                                source: 'New Campaign Button - Dashboard'
                            })
                            track("Campaign Started", {
                                brand_id: currentBrand?.id,
                                brand_name: currentBrand?.name,
                                brand_slug: currentBrand?.slug,
                                from: 'New Campaign Button - Dashboard',
                            });
                        }}
                        width="100%"
                        size="small"
                        iconLeft="plus"
                        iconLeftSize={18}
                        iconLeftOffset={-6}
                    >
                        New Campaign
                    </ButtonSymphony>
                </div>

                {/* {renderUpdateNotifications()} */}
                {renderSideContentInner()}
            </div>
        )
    }


    /** Handle closing the welcome modal */
    const handleCloseWelcomeModal = () => {
        setCookie('hasSeenWelcomeModal')
        setShowWelcomeModal(false)
    };

    const getDashboardLeftContainerWidth = () => {
        if (smallView || mediumView || !isBrandAdmin) {
            return '100%'
        }

        return 'calc(100% - 356px)'
    }

    const renderPlatformGraphs = () => {
        var lastDaysString = "Last 7 Days"
        var interval: any = "preserveEnd"
        if (timeRange === 'last-30-days') {
            lastDaysString = "Last 30 Days"
            interval = 6
        } else if (timeRange === "last-3-months") {
            lastDaysString = "Last 3 Months"
            interval = 9
        } else if (timeRange === 'custom') {
            interval = "preserveStart"
            lastDaysString = `${dayjs(selectedDate.startDate).format('M/D')} to ${dayjs(selectedDate.endDate).format('M/D')}`
        }

        if (window.innerWidth < 576) {
            interval = "preserveEnd"
        }

        const graphData = platformGraphsData.map((platformData: GraphPresentation, idx: number) => {
            const {
                platformId,
                platformName,
                chartData,
                currentValue,
                changeValue,
                imageAsset,
                statName,
                isConnected,
                decommissionMessage = null
            } = platformData

            // NOTE: we are not supporting the platform anymore
            if (isConnected && decommissionMessage) {
                return null
            }

            if (!isConnected) {
                return (
                    <div
                        key={idx}
                        className="overallDashboardPlatformCard"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            width: '100%',
                            position: 'relative',
                            flex: 1,
                            border: Sty.border,
                            borderRadius: 8,
                        }}
                    >
                        <div
                            className="imageAssetOuterContainer"
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 8,
                                paddingTop: 8,
                                paddingLeft: 16,
                                paddingRight: 16,
                                paddingBottom: 8,
                            }}
                        >
                            <img
                                className="imageDashboardAssetIcon"
                                style={{ flexShrink: 0 }}
                                width={50}
                                src={imageAsset}
                                alt="imageDashboardAssetIcon"
                            />
                            <div className="platformInfoContainer">
                                <p className="platformInfoTitle" style={Sty.text16bold}>
                                    Track {platformName} {statName}
                                </p>
                                <p className="platformInfoSubtitle" style={Sty.text12grey}>
                                    Connect your {platformName} account to track your {statName}.
                                </p>
                            </div>
                        </div>
                        {isBrandAdmin &&
                            <ButtonSymphony
                                className="overallDashboardConnectButton"
                                onClick={() => setSelectedPlatformForConnect(platformId as SocialConnectType)}
                                variant="outlined"
                                style={{
                                    marginTop: 8,
                                    marginBottom: 16,
                                    alignSelf: 'center'
                                }}
                            >
                                Connect
                            </ButtonSymphony>
                        }
                    </div>
                )
            }

            return (
                <SmallGraph
                    key={idx}
                    index={idx}
                    expandGraph={() => setShowLargeGraph(platformData)}
                    platformName={platformName}
                    imageAsset={imageAsset}
                    primaryValue={currentValue && millify(currentValue)}
                    subtitle={statName}
                    changeValue={changeValue ? changeValue : 0}
                    graphData={chartData}
                />
            )
        })

        return (
            <>
                <div
                    className="overallDashboardPlatformsContainer"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginTop: 24
                    }}
                >
                    <div className="dashboardPlatformsHeaderContainer" style={{ paddingRight: 16 }}>
                        <div style={Sty.text20}>
                            Platforms ({lastDaysString})
                        </div>
                        <div style={Sty.text16grey}>
                            The total change in fans {timeRange === 'custom' ? 'from ' : 'over the '} {lastDaysString.toLowerCase()}.
                        </div>
                    </div>
                    {isBrandAdmin &&
                        <ButtonSymphony
                            className="overallDashboardEditGearButton"
                            onClick={() => setShowSocialConnectModal(true)}
                            aria-label="edit"
                            variant="text"
                            iconRight="gear"
                        >
                            {mobileView ? "Edit" : "Edit Platforms"}
                        </ButtonSymphony>
                    }
                </div>
                <div
                    className="overallDashboardPlatformsCardsContainer"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: mediumView ? '1fr' : 'repeat(2, 1fr)',
                        gap: 8,
                        marginTop: 16,
                        width: '100%',
                    }}
                >
                    {graphData}
                </div>
            </>
        )
    }

    const handleTikTokAuth = async (authCode: string) => {
        setIsTikTokConnecting(true);
        setTikTokError(null);

        try {
            await connectToBrand_api({
                brandId: currentBrand?.id,
                brandSlug: currentBrand?.slug,
                value: { code: authCode },
                service: 'tiktok_auth'
            });

            // Clear auth code and close modal on success
            localStorage.removeItem('tt_auth_code');
            setShowTikTokAuthModal(false);

            // Close the popup window
            if (window.opener) {
                window.opener.close();
            }
        } catch (error) {
            console.error('Error connecting TikTok:', error);
            setTikTokError('Failed to connect your TikTok account. Please try again.');
        } finally {
            setIsTikTokConnecting(false);
        }
    }

    const renderPlaybooks = () => {
        if (!isBrandAdmin) {
            return null;
        }

        // Hide playbooks on mobile since quick actions are now in Toolkit popup
        if (mediumView) {
            return null;
        }

        return (
            <div
                className="playbooksContainer"
                style={{
                    width: '100%',
                    paddingTop: mediumView ? 16 : 0,
                    paddingLeft: mediumView ? 16 : 0,
                    paddingRight: mediumView ? 16 : 0,
                }}
            >
                <div
                    className="playbooksScrollContainer"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        overflowX: 'scroll',
                        width: '100%',
                        scrollbarWidth: 'none',
                    }}
                >
                    {PLAYBOOKS_FOR_DASHBOARD
                        .filter(playbook => {
                            // If the playbook is only for Symphony Insiders, check if the user is a Symphony Insider
                            if (playbook.onlyForSymphonyBetaTestUsers) {
                                return isSymphonyBetaTestUser;
                            }
                            if (playbook.hidden) {
                                return false;
                            }
                            return true;
                        })
                        .map((playbook, index) => {
                            const {
                                name,
                                icon,
                                title,
                                background,
                                onClick,
                            } = playbook;

                            return (
                                <div
                                    key={makeKey(name, index)}
                                    className="playbookCard"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        borderRadius: 8,
                                        paddingTop: 12,
                                        paddingLeft: 8,
                                        paddingRight: 8,
                                        paddingBottom: 8,
                                        alignItems: 'flex-start',
                                        flexShrink: 0,
                                        scrollbarWidth: 'none',
                                        background,
                                        width: 150,
                                        height: 120,
                                    }}
                                >
                                    <div
                                        className="playbookCardImageContainer"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: 8,
                                        }}
                                    >
                                        <Icon
                                            className="playbookCardSocialIcon"
                                            icon={icon}
                                            color="white"
                                            size={20}
                                        />
                                        <div
                                            className="playbookCardTitle"
                                            style={{
                                                ...Sty.text16,
                                                color: 'white',
                                                whiteSpace: 'break-spaces',
                                            }}
                                        >
                                            {title}
                                        </div>
                                    </div>

                                    <ButtonSymphony
                                        className="playbookButton"
                                        style={{
                                            marginTop: 'auto',
                                            paddingLeft: 10,
                                            paddingRight: 10,
                                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                            marginLeft: 'auto',
                                            marginRight: 'auto',
                                            color: "white"
                                        }}
                                        size="small"
                                        onClick={onClick}
                                        color="white"

                                        iconLeft="stars"
                                        iconLeftOffset={-3}
                                        iconLeftSize={16}
                                        iconLeftColor="white"

                                        iconRight="chevronRight"
                                        iconRightOffset={-3}
                                        iconRightSize={14}
                                        iconRightColor="white"
                                    >
                                        Get started
                                    </ButtonSymphony>
                                </div>
                            )
                        })}
                </div>
            </div>
        )
    }



    const renderPageContent = () => {
        // Always render the traditional dashboard for all users
        return (
            <PageSymphony
                className="dashboardComponent"
                isProMobileNavCta
            >
                <ContentSymphony>
                    {/* Only render BannerContainer if at least one banner needs to be shown */}
                    <div className="dashboardInnerContainer" style={mStyle(sty, 'dashboardInnerContainer')}>
                        <div
                            className="dashboardInnerContainerLeft"
                            style={styx(
                                mStyle(sty, 'dashboardInnerContainerLeft'),
                                { width: getDashboardLeftContainerWidth() }
                            )}
                        >
                            {mediumView && (
                                <CampaignsSidebar
                                    className="dashboardCampaignsSidebarMobile"
                                    campaigns={adaptCampaignsForSidebar(campaignsRecent)}
                                    isLoading={isLoadingCampaignsRecent}
                                    showBrandInfo={false}
                                    showRecommendations={true}
                                />
                            )}
                            {mediumView && showRecommendationsSidebar &&
                                <Recommendations isBanner />
                            }
                            <div
                                className="overallDashboardContainer"
                                style={mStyle(sty, 'overallDashboardContainer')}
                            >
                                {isLoadingInitialData ?
                                    <Loader
                                        className="loadingDashboardLoader"
                                        size={32}
                                        title="Loading Dashboard..."
                                        isBackground
                                    />
                                    :
                                    <>
                                        {renderOverallDashboard()}
                                        {renderPlatformGraphs()}
                                    </>
                                }
                            </div>
                        </div>
                        {!mediumView && showRecommendationsSidebar &&
                            renderDashboardSideContent()
                        }
                        {!mediumView && SHOW_ACTIVE_CAMPAIGNS &&
                            renderDashboardSideContent()
                        }
                    </div>
                </ContentSymphony>
            </PageSymphony>
        )
    }

    const renderConnectionsModals = () => {
        if (selectedPlatformForConnect === SocialConnectType.FACEBOOK) {
            return (
                <AdConnectionModal
                    subtitle="Connect your Facebook Page to track followers."
                    isOpen={!!selectedPlatformForConnect}
                    onClose={() => setSelectedPlatformForConnect(null)}
                    connectionType={AdConnectionType.FACEBOOK_PAGE}
                />
            )
        }
        return (
            <>
                {selectedPlatformForConnect &&
                    <SocialConnectModal
                        className="socialConnectModalOverallDashboard"
                        isOpen={!!selectedPlatformForConnect}
                        onClose={() => setSelectedPlatformForConnect(null)}
                        socialConnectType={selectedPlatformForConnect}
                        validateBusinessAccount
                    />
                }
            </>
        )
    }

    // NOTE: render()
    return (
        <>
            {renderPageContent()}

            {/* RENDER: MODALS */}
            <PlatformsConnector
                isOpen={showSocialConnectModal}
                closeModal={() => setShowSocialConnectModal(false)}
            />

            {currentBrand &&
                <UpgradeToProSucceededModal
                    dialogProps={{
                        open: showUpgradeToProSucceededModal,
                        onClose: closeUpgradeToProSucceededModal,
                    }}
                    sessionId={sessionId}
                />
            }

            {/* NOTE: modals */}
            {renderLargeGraphPopup()}
            {renderConnectionsModals()}
            <ModalSymphony
                className="tiktokAuthModal"
                isOpen={showTikTokAuthModal}
                onClose={() => {
                    setShowTikTokAuthModal(false);
                    localStorage.removeItem('tt_auth_code');
                }}
                maxWidth={400}
            >
                <div style={{ padding: 24, textAlign: 'center' }}>
                    {isTikTokConnecting ? (
                        <>
                            <Loader />
                            <p style={{ marginTop: 16 }}>Connecting your TikTok account...</p>
                        </>
                    ) : tikTokError ? (
                        <>
                            <p style={{ color: Colors.red }}>{tikTokError}</p>
                            <ButtonSymphony
                                className="tiktokAuthModalCloseButton"
                                onClick={() => {
                                    setShowTikTokAuthModal(false);
                                    localStorage.removeItem('tt_auth_code');
                                }}
                                style={{ marginTop: 16 }}
                            >
                                Close
                            </ButtonSymphony>
                        </>
                    ) : (
                        <p>TikTok connection completed successfully!</p>
                    )}
                </div>
            </ModalSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    dashboardComponent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        paddingTop: 24,
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 24,
        marginBottom: 0,
    },
    dashboardComponent_medium: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
    },
    overallDashboardContainer: {
        backgroundColor: 'white',
        paddingLeft: 24,
        paddingRight: 24,
        paddingTop: 24,
        paddingBottom: 24,
        width: '100%',
        maxWidth: 1200,
        borderRadius: 8,
    },
    overallDashboardContainer_mobile: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
        borderRadius: 0,
    },
    overallDashboardContainer_medium: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
    },
    dashboardInnerContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        maxWidth: 1200,
        width: '100%',
    },
    dashboardInnerContainer_medium: {
        flexDirection: 'column',
    },
    dashboardInnerContainerLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    dashboardSideContentContainer: {
        position: 'sticky',
        top: 24,
        backgroundColor: 'white',
        padding: 16,
        width: 340,
        borderRadius: 8,
        flexShrink: 0,
        alignSelf: 'flex-start',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
    },
    dashboardSideContentContainer_medium: {
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 32,
    },
    sideDashboardButtonsContainer: {
        position: 'sticky',
        top: -16,
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        marginTop: -16,
        marginBottom: 16,
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: Sty.border,
    },
    dashboardSideItemsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    dashboardSideItemImage: {
        width: '100%',
        height: 120,
        borderRadius: 12,
    },
    dashboardSideItemDescription: {
        color: Colors.greyDark,
        display: '-webkit-box',
        cursor: 'pointer',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: '1.2em',
        maxHeight: '2.4em',
    },
    dashboardSideItemDescriptionContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    updateNotificationsCount: {
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: 9,
        paddingRight: 9,
        backgroundColor: Colors.purple,
        color: 'white',
        borderRadius: 8,
        width: 'fit-content',
    },
    updateNotificationsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
    },


}

export default Dashboard;